import { useDebounce } from '@uidotdev/usehooks';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { getContactDisplayName } from '@/components/postgrid/common/contactUtils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { enhancedApi } from '@/services/api/generated/postgridService';

import { ContactComboboxItem } from './ContactComboboxItem';

const { usePostgridContactsListQuery } = enhancedApi;

interface ContactComboboxProps {
  value?: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  /** Display name to show when contact is not found in the list (e.g., newly created contact) */
  selectedDisplayName?: string | null;
}

/**
 * Contact selector using shadcn/ui Command + Popover pattern.
 *
 * Features:
 * - Server-side search with 300ms debouncing
 * - Shows all contacts by default, filters as you type
 * - Loading states and empty state messaging
 * - Keyboard navigation and accessibility
 */
export const ContactCombobox: React.FC<ContactComboboxProps> = ({
  value,
  onChange,
  placeholder = 'Select contact…',
  disabled = false,
  className,
  'aria-label': ariaLabel,
  selectedDisplayName,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Debounce search to avoid spamming the API
  const debouncedSearch = useDebounce(search, 300);

  // Only fetch contacts when user has typed something (performance optimization)
  const shouldFetch = debouncedSearch.trim().length > 0;

  // Fetch contacts from API
  const {
    data: contactsResponse,
    isFetching,
    isLoading,
  } = usePostgridContactsListQuery(
    {
      q: debouncedSearch || undefined,
      isActive: true,
      limit: 50, // Reduced from 100 for faster rendering
    },
    {
      skip: !shouldFetch, // Don't fetch until user starts typing
      refetchOnMountOrArgChange: false, // Only refetch when query changes
    },
  );

  // Handle paginated response format
  const contacts = contactsResponse?.results || [];

  // Find selected contact for display
  const selectedContact = useMemo(() => {
    if (!value) return null;
    return contacts.find((contact) => contact.id === value) || null;
  }, [value, contacts]);

  // Handle selection
  const handleSelect = useCallback(
    (contactId: string) => {
      onChange(contactId);
      setOpen(false);
    },
    [onChange],
  );

  // Handle search input changes
  const handleSearchChange = useCallback((searchValue: string) => {
    setSearch(searchValue);
  }, []);

  // Handle popover open/close
  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearch(''); // Clear search when closing
    }
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel || 'Select contact'}
          className={cn(
            'justify-between',
            !selectedContact && 'text-muted-foreground',
            className,
          )}
          disabled={disabled}
        >
          {selectedContact
            ? getContactDisplayName(selectedContact)
            : value && selectedDisplayName
              ? selectedDisplayName
              : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search contacts…"
            value={search}
            onValueChange={handleSearchChange}
          />
          <CommandList>
            {/* Loading state */}
            {(isLoading || isFetching) && (
              <div className="flex items-center gap-2 py-6 px-4 text-sm text-muted-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
                <span>Searching…</span>
              </div>
            )}

            {/* Empty state */}
            {!isLoading &&
              !isFetching &&
              (!search.trim() ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Start typing to search contacts
                </div>
              ) : contacts.length === 0 ? (
                <CommandEmpty>
                  <div className="py-6 text-center">
                    <div className="text-sm">No contacts found</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Try a different search term
                    </div>
                  </div>
                </CommandEmpty>
              ) : null)}

            {/* Results */}
            {contacts.length > 0 && (
              <CommandGroup>
                {contacts.map((contact) => (
                  <CommandItem
                    key={contact.id}
                    value={contact.id}
                    onSelect={handleSelect}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        'h-4 w-4 mr-2 shrink-0',
                        value === contact.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <ContactComboboxItem contact={contact} />
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
